/**
 * Signup (Sprint 5 B5) — page đăng ký tài khoản mới.
 *
 * Flow:
 *   1. User điền form (email, password, name)
 *   2. POST /api/auth/register/ → BE tạo user inactive + Celery email
 *   3. Show success state "Kiểm tra email để xác thực"
 *   4. User click link trong email → /verify-email/<token>
 *
 * Anti-enumeration: BE luôn trả 201 generic dù email tồn tại hay không,
 * FE chỉ hiển thị "Kiểm tra email" — không leak.
 */

import { useState } from "react";
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Result,
  Space,
  Typography,
} from "antd";
import { LockOutlined, MailOutlined, UserOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { register, type RegisterRequest } from "@/api/auth";

const { Title, Text, Paragraph } = Typography;

export function Signup() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (values: RegisterRequest) => {
    setLoading(true);
    setError(null);
    try {
      await register(values);
      setSubmittedEmail(values.email);
      setSubmitted(true);
    } catch (err: unknown) {
      const resp = (err as { response?: { status?: number; data?: Record<string, unknown> } })
        ?.response;
      if (resp?.status === 429) {
        setError("Quá nhiều lượt đăng ký từ IP này. Vui lòng thử lại sau 1 giờ.");
      } else if (resp?.data?.password) {
        // Password validator fail (weak)
        const msgs = Array.isArray(resp.data.password)
          ? resp.data.password.join(" ")
          : String(resp.data.password);
        setError(`Mật khẩu yếu: ${msgs}`);
      } else {
        setError("Đăng ký thất bại. Vui lòng thử lại.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f4f5f7",
        }}
      >
        <Card style={{ width: 480, padding: "16px 8px" }}>
          <Result
            status="success"
            title="Kiểm tra email để xác thực"
            subTitle={
              <Paragraph>
                Em đã gửi link xác thực tới <strong>{submittedEmail}</strong>.
                Link có hiệu lực trong 24 giờ. Nếu không thấy, vui lòng kiểm tra
                thư mục Spam.
              </Paragraph>
            }
            extra={[
              <Link to="/login" key="login">
                <Button type="primary">Về trang đăng nhập</Button>
              </Link>,
            ]}
          />
        </Card>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#f4f5f7",
      }}
    >
      <Card style={{ width: 480, padding: "16px 8px" }}>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div style={{ textAlign: "center" }}>
            <Title level={2} style={{ margin: 0 }}>
              Đăng ký tài khoản
            </Title>
            <Text type="secondary">
              Tham gia F-Future E-learning Platform để học môn học chất lượng
            </Text>
          </div>

          {error && <Alert type="error" message={error} showIcon closable />}

          <Form<RegisterRequest>
            layout="vertical"
            onFinish={handleSubmit}
            requiredMark={false}
            autoComplete="off"
          >
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: "Vui lòng nhập email." },
                { type: "email", message: "Email không hợp lệ." },
              ]}
            >
              <Input
                prefix={<MailOutlined />}
                placeholder="email@example.com"
                size="large"
                autoComplete="email"
              />
            </Form.Item>

            <Space style={{ width: "100%" }} size="small">
              <Form.Item
                name="last_name"
                label="Họ"
                style={{ width: "100%" }}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="Nguyễn"
                  size="large"
                />
              </Form.Item>
              <Form.Item
                name="first_name"
                label="Tên"
                style={{ width: "100%" }}
              >
                <Input placeholder="An" size="large" />
              </Form.Item>
            </Space>

            <Form.Item
              name="password"
              label="Mật khẩu"
              rules={[
                { required: true, message: "Vui lòng nhập mật khẩu." },
                { min: 10, message: "Mật khẩu tối thiểu 10 ký tự." },
              ]}
              extra="Tối thiểu 10 ký tự, không quá phổ biến, không toàn số."
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="••••••••••"
                size="large"
                autoComplete="new-password"
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                size="large"
              >
                Đăng ký
              </Button>
            </Form.Item>
          </Form>

          <div style={{ textAlign: "center" }}>
            <Text type="secondary">Đã có tài khoản? </Text>
            <Link to="/login">Đăng nhập</Link>
          </div>
        </Space>
      </Card>
    </div>
  );
}

export default Signup;
